import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Linkedin, Facebook, Send } from "lucide-react";
import { COMPANY } from "../mock/mock";
import Logo from "./Logo";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !consent) {
      toast({ title: "Information manquante", description: "Veuillez entrer votre courriel et accepter de recevoir nos communications." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast({ title: "Merci !", description: "Vous avez été ajouté à notre liste de diffusion." });
      setEmail("");
      setConsent(false);
    } catch (err) {
      toast({ title: "Erreur", description: "L'inscription a échoué. Veuillez réessayer." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#0f2740] text-white/80">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
        <div>
          <Logo variant="light" />
          <p className="mt-6 text-sm leading-relaxed text-white/70 max-w-xs">
            Cabinet de conseil spécialisé en gouvernance des TI et en transformation agile, au service de la performance de votre organisation.
          </p>
          <div className="flex items-center gap-3 mt-6">
            <a href={COMPANY.social.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#2f80c4] transition-colors" aria-label="Facebook">
              <Facebook size={17} />
            </a>
            <a href={COMPANY.social.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#2f80c4] transition-colors" aria-label="LinkedIn">
              <Linkedin size={17} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold uppercase tracking-wide text-sm mb-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Pour nous joindre
          </h4>
          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <MapPin size={18} className="text-[#9bc3e6] mt-0.5 shrink-0" />
              <span>{COMPANY.address}</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={18} className="text-[#9bc3e6] shrink-0" />
              <a href={`tel:${COMPANY.phoneHref}`} className="hover:text-white transition-colors">{COMPANY.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={18} className="text-[#9bc3e6] shrink-0" />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-white transition-colors">{COMPANY.email}</a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold uppercase tracking-wide text-sm mb-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Infolettre
          </h4>
          <p className="text-sm text-white/70 mb-4 leading-relaxed">
            Recevez périodiquement nos communications sur la gouvernance TI, l'agilité et nos formations.
          </p>
          <form onSubmit={handleSubscribe} className="space-y-3">
            <div className="flex">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre courriel"
                className="flex-1 px-4 py-2.5 rounded-l bg-white/10 border border-white/15 text-white placeholder-white/50 text-sm focus:outline-none focus:border-[#2f80c4]"
              />
              <button type="submit" disabled={submitting} className="px-4 bg-[#2f80c4] hover:bg-[#3d92d6] rounded-r transition-colors disabled:opacity-60" aria-label="S'abonner">
                <Send size={17} className={`text-white ${submitting ? "animate-pulse" : ""}`} />
              </button>
            </div>
            <label className="flex items-start gap-2 text-xs text-white/60 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#2f80c4]" />
              <span>J'accepte de recevoir les communications d'Agile Connexion.</span>
            </label>
          </form>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/55">
          <span>© {new Date().getFullYear()} {COMPANY.legalName} — Tous droits réservés.</span>
          <div className="flex items-center gap-5">
            <Link to="/nous-joindre" className="hover:text-white transition-colors">Nous joindre</Link>
            <span className="opacity-40">|</span>
            <span>Politique de confidentialité</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
